/**
 * DONATION SERVICE
 * 
 * Handles all API calls for donation operations.
 * 
 * @module Donations/Services
 */

import { Donation, CreateDonationDTO, DonationStats } from '../types/donation.types';

const API_BASE = '/.netlify/functions';

/**
 * Get authentication token from local storage
 */
const getToken = (): string | null => {
    try {
        return localStorage.getItem('adminToken');
    } catch {
        return null;
    }
};

/**
 * Handle API response
 */
const handleResponse = async <T>(response: Response): Promise<T> => {
    if (response.status === 401) {
        localStorage.removeItem('adminToken');
        window.location.href = '/admin';
        throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `API Error: ${response.status}`);
    }

    return response.json();
};

/**
 * Get auth headers
 */
const getAuthHeaders = (): HeadersInit => {
    const token = getToken();
    if (!token) {
        throw new Error('No authentication token found');
    }
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

export const donationService = {
    /**
     * Fetch all donations
     */
    async getDonations(): Promise<Donation[]> {
        const headers = getAuthHeaders();
        const response = await fetch(`${API_BASE}/admin-donations`, {
            method: 'GET',
            headers
        });
        const data = await handleResponse<{ data: Donation[] }>(response);
        return data.data || [];
    },

    /**
     * Fetch a single donation by ID
     */
    async getDonation(id: string): Promise<Donation> {
        const headers = getAuthHeaders();
        const response = await fetch(`${API_BASE}/admin-donations/${id}`, {
            method: 'GET',
            headers
        });
        const data = await handleResponse<{ data: Donation }>(response);
        return data.data;
    },

    /**
     * Create a new donation
     */
    async createDonation(donation: CreateDonationDTO): Promise<Donation> {
        const headers = getAuthHeaders();
        const response = await fetch(`${API_BASE}/admin-donations`, {
            method: 'POST',
            headers,
            body: JSON.stringify(donation)
        });
        const data = await handleResponse<{ data: Donation }>(response);
        return data.data;
    },

    /**
     * Update a donation
     */
    async updateDonation(id: string, updates: Partial<Donation>): Promise<Donation> {
        const headers = getAuthHeaders();
        const response = await fetch(`${API_BASE}/admin-donations/${id}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify(updates)
        });
        const data = await handleResponse<{ data: Donation }>(response);
        return data.data;
    },

    /**
     * Delete a donation
     */
    async deleteDonation(id: string): Promise<void> {
        const headers = getAuthHeaders();
        const response = await fetch(`${API_BASE}/admin-donations/${id}`, {
            method: 'DELETE',
            headers
        });
        await handleResponse(response);
    },

    /**
     * Get donation statistics
     */
    async getDonationStats(): Promise<DonationStats> {
        const headers = getAuthHeaders();
        const response = await fetch(`${API_BASE}/admin-donations/stats`, {
            method: 'GET',
            headers
        });
        const data = await handleResponse<{ data: DonationStats }>(response);
        return data.data;
    },

    /**
     * Export donations to CSV
     */
    async exportDonations(): Promise<Blob> {
        const headers = getAuthHeaders();
        const response = await fetch(`${API_BASE}/admin-donations/export`, {
            method: 'GET',
            headers: {
                ...headers,
                'Accept': 'text/csv'
            }
        });
        
        if (!response.ok) {
            throw new Error('Failed to export donations');
        }
        
        return response.blob();
    }
};
